import { FormFieldConfig } from '@shared/components/search-form/search-form.component';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import {
  FormField,
  ActionButton,
  TableSection,
  SectionButton,
} from '@shared/components/add-form/add-form.component';
import { SYNCHRONIZATION_STATUS_OPTIONS } from './models/synchronization.model';

// ============================================================================
// INTERFACES
// ============================================================================

export interface SynchronizationFormConfig {
  title: string;
  formFields: FormField[];
  headerActions: ActionButton[];
  tableSections?: TableSection[];
  listRoute: string;
}

// ============================================================================
// SEARCH FORM CONFIGURATIONS
// ============================================================================

export const SYNCHRONIZATION_SEARCH_CONFIG: FormFieldConfig[] = [
  {
    key: 'warehouseCode',
    label: 'synchronization.search.fields.warehouse_code',
    type: 'text',
    placeholder: 'synchronization.search.placeholders.enter_warehouse_code',
  },
  {
    key: 'warehouseName',
    label: 'synchronization.search.fields.warehouse_name',
    type: 'text',
    placeholder: 'synchronization.search.placeholders.enter_warehouse_name',
  },
  {
    key: 'status',
    label: 'synchronization.search.fields.status',
    type: 'multiselect',
    options: SYNCHRONIZATION_STATUS_OPTIONS,
    placeholder: 'synchronization.search.placeholders.select_status',
  },
  // {
  //   key: 'syncTime',
  //   label: 'synchronization.search.fields.sync_time',
  //   type: 'dateRange',
  //   placeholder: 'synchronization.search.placeholders.select_sync_time',
  // },
];

// ============================================================================
// TABLE COLUMN CONFIGURATIONS
// ============================================================================

/**
 * Default column property names for Synchronization table
 */
export const SYNCHRONIZATION_DEFAULT_COLUMNS = {
  ADJUST_CODE: 'adjustCode',
  WAREHOUSE_CODE: 'warehouseCode',
  WAREHOUSE_NAME: 'warehouseName',
  STATUS: 'status',
  LAST_UPDATE_DATE: 'lastUpdateDate',
  REMARK: 'remarks',
} as const;

/**
 * Flexible column property names for Synchronization table
 */
export const SYNCHRONIZATION_FLEXIBLE_COLUMNS = {
  CREATION_DATE: 'creationDate',
} as const;

/**
 * Action column property name for Synchronization table
 */
export const SYNCHRONIZATION_ACTION_COLUMN = {
  ACTIONS: 'actions',
} as const;

/**
 * Default columns order for Synchronization table
 */
export const SYNCHRONIZATION_DEFAULT_COLUMNS_ORDER: string[] = [
  SYNCHRONIZATION_DEFAULT_COLUMNS.ADJUST_CODE,
  SYNCHRONIZATION_DEFAULT_COLUMNS.WAREHOUSE_CODE,
  SYNCHRONIZATION_DEFAULT_COLUMNS.WAREHOUSE_NAME,
  SYNCHRONIZATION_DEFAULT_COLUMNS.STATUS,
  SYNCHRONIZATION_DEFAULT_COLUMNS.LAST_UPDATE_DATE,
  SYNCHRONIZATION_DEFAULT_COLUMNS.REMARK,
];

/**
 * Flexible columns array for Synchronization table
 */
export const SYNCHRONIZATION_FLEXIBLE_COLUMNS_ORDER: string[] = [
  SYNCHRONIZATION_FLEXIBLE_COLUMNS.CREATION_DATE,
];

export const SYNCHRONIZATION_COLUMNS: TableColumn[] = [
  {
    label: 'synchronization.table.columns.data_sync_number',
    prop: SYNCHRONIZATION_DEFAULT_COLUMNS.ADJUST_CODE,
    width: 150,
  },
  {
    label: 'synchronization.table.columns.warehouse_code',
    prop: SYNCHRONIZATION_DEFAULT_COLUMNS.WAREHOUSE_CODE,
    width: 150,
  },
  {
    label: 'synchronization.table.columns.warehouse_name',
    prop: SYNCHRONIZATION_DEFAULT_COLUMNS.WAREHOUSE_NAME,
    width: 250,
  },
  {
    label: 'synchronization.table.columns.status',
    prop: SYNCHRONIZATION_DEFAULT_COLUMNS.STATUS,
    width: 120,
    type: 'status',
  },
  {
    label: 'synchronization.table.columns.operation_time',
    prop: SYNCHRONIZATION_DEFAULT_COLUMNS.LAST_UPDATE_DATE,
    width: 135,
    type: 'custom',
    template: 'operationTimeTemplate',
  },
  {
    label: 'synchronization.table.columns.remark',
    prop: SYNCHRONIZATION_DEFAULT_COLUMNS.REMARK,
    width: 150,
  },
  {
    label: 'synchronization.table.columns.creation_date',
    prop: SYNCHRONIZATION_FLEXIBLE_COLUMNS.CREATION_DATE,
    width: 135,
    type: 'custom',
    template: 'dateTemplate',
  },
  {
    label: 'synchronization.table.columns.actions',
    prop: SYNCHRONIZATION_ACTION_COLUMN.ACTIONS,
    type: 'actions',
    width: 250,
    align: 'center',
  },
];

// ============================================================================
// FORM FIELD CONFIGURATIONS
// ============================================================================

export const SYNCHRONIZATION_FORM_FIELDS: FormField[] = [
  {
    label: 'synchronization.form.fields.logical_data_sync_number',
    controlName: 'adjustCode',
    type: 'text',
    placeholder: 'synchronization.form.placeholders.system_generated',
    section: 'synchronization.form.sections.basic_information',
    readonly: true,
    disabled: true,
  },
  {
    label: 'synchronization.form.fields.warehouse_id',
    controlName: 'warehouseId',
    type: 'text',
    placeholder: 'synchronization.form.placeholders.warehouse_id',
    section: 'synchronization.form.sections.basic_information',
    readonly: true,
    disabled: true,
    hidden: true,
  },
  {
    label: 'synchronization.form.fields.warehouse_code',
    controlName: 'warehouseCode',
    type: 'text',
    placeholder: 'synchronization.form.placeholders.warehouse_code',
    section: 'synchronization.form.sections.basic_information',
    readonly: true,
    disabled: true,
  },
  {
    label: 'synchronization.form.fields.warehouse_name',
    controlName: 'warehouseName',
    type: 'text',
    placeholder: 'synchronization.form.placeholders.warehouse_name',
    section: 'synchronization.form.sections.basic_information',
    readonly: true,
    disabled: true,
  },
  {
    label: 'synchronization.form.fields.remark',
    controlName: 'remarks',
    type: 'textarea',
    placeholder: 'synchronization.form.placeholders.enter_remark',
    section: 'synchronization.form.sections.basic_information',
    maxLength: 500,
    colspan: 2,
  },
];

// ============================================================================
// TABLE SECTION CONFIGURATIONS
// ============================================================================

export const SYNCHRONIZATION_TABLE_SECTIONS: TableSection[] = [
  {
    title: 'synchronization.form.sections.product_details',
    show: true,
    hasCheckbox: true,
    data: [],
    columns: [
      {
        prop: 'categoryName',
        label: 'synchronization.table.columns.product_category',
        width: 150,
        type: 'text',
      },
      {
        prop: 'spuCode',
        label: 'synchronization.table.columns.product_code',
        width: 150,
        type: 'text',
      },
      {
        prop: 'spuName',
        label: 'synchronization.table.columns.product_name',
        width: 200,
        type: 'text',
      },
      {
        prop: 'skuCode',
        label: 'synchronization.table.columns.sku_code',
        width: 150,
        type: 'text',
      },
      {
        prop: 'adjustmentType',
        label: 'synchronization.table.columns.adjustment_type',
        width: 150,
        type: 'select',
        placeholder: 'synchronization.placeholders.select_type',
        required: true,
        options: [
          { label: 'synchronization.adjust_type.increase', value: 'INCREASE' },
          { label: 'synchronization.adjust_type.decrease', value: 'DECREASE' },
        ],
      },
      {
        prop: 'batchId',
        label: 'inbound.fields.lot',
        width: 180,
        type: 'inputText',
      },
      {
        prop: 'adjustmentQuantity',
        label: 'synchronization.table.columns.adjustment_quantity',
        width: 180,
        type: 'inputNumber',
        showButtons: true,
        placeholder: 'synchronization.placeholders.enter_quantity',
        min: 0,
        step: 1,
        required: true,
      },
    ],
    actions: [
      {
        label: 'synchronization.table.actions.select_products',
        action: 'selectProducts',
        severity: 'info',
      },
      {
        label: 'synchronization.table.actions.delete_products',
        action: 'deleteSelected',
        severity: 'danger',
        outlined: true,
        disabled: true,
      },
    ],
  },
];

// ============================================================================
// HEADER ACTIONS
// ============================================================================

export const SYNCHRONIZATION_HEADER_ACTIONS: ActionButton[] = [
  {
    label: 'synchronization.form.actions.cancel',
    action: 'cancel',
    outlined: true,
    severity: 'secondary',
  },
  {
    label: 'synchronization.form.actions.save',
    action: 'save',
    severity: 'info',
  },
];

// View mode header actions (only back button)
export const SYNCHRONIZATION_HEADER_ACTIONS_VIEW: ActionButton[] = [
  {
    label: 'common.actions.back',
    action: 'cancel',
    outlined: true,
    severity: 'secondary',
  },
];

export const SYNCHRONIZATION_SECTION_BUTTONS: SectionButton[] = [
  {
    label: 'synchronization.form.actions.select_logical_warehouse',
    action: 'selectWarehouse',
    severity: 'info',
    section: 'synchronization.form.sections.basic_information',
  },
];

// ============================================================================
// SHARED COLUMN CONFIGURATIONS
// ============================================================================

// Base columns shared between main table and approval details dialog
export const SYNCHRONIZATION_BASE_COLUMNS: TableColumn[] = [
  {
    prop: 'categoryName',
    label: 'synchronization.table.columns.product_category',
    width: 150,
    type: 'text',
  },
  {
    prop: 'spuCode',
    label: 'synchronization.table.columns.product_code',
    width: 150,
    type: 'text',
  },
  {
    prop: 'spuName',
    label: 'synchronization.table.columns.product_name',
    width: 200,
    type: 'text',
  },
  {
    prop: 'skuCode',
    label: 'synchronization.table.columns.sku_code',
    width: 150,
    type: 'text',
  },
  {
    prop: 'skuName',
    label: 'synchronization.table.columns.sku_name',
    width: 250,
    type: 'text',
  },
  {
    prop: 'adjustmentType',
    label: 'synchronization.table.columns.adjustment_type',
    width: 120,
    type: 'custom',
  },
  {
    prop: 'planNum',
    label: 'synchronization.table.columns.plan_num',
    width: 150,
    type: 'text',
  },
];

// Main synchronization table columns (read-only)
export const SYNCHRONIZATION_TABLE_COLUMNS: TableColumn[] = [
  ...SYNCHRONIZATION_BASE_COLUMNS,
  {
    prop: 'realityNum',
    label: 'synchronization.table.columns.reality_num',
    width: 150,
    type: 'text',
  },
  {
    prop: 'cancelNum',
    label: 'synchronization.table.columns.cancel_num',
    width: 150,
    type: 'text',
  },
];

// Approval details dialog columns (with editable fields)
export const APPROVAL_DETAILS_TABLE_COLUMNS: TableColumn[] = [
  ...SYNCHRONIZATION_BASE_COLUMNS,
  {
    prop: 'batchId',
    label: 'inbound.fields.lot',
    width: 180,
    type: 'inputText',
  },
  {
    prop: 'realityNum',
    label: 'synchronization.table.columns.reality_num',
    width: 200,
    type: 'inputNumber',
    showButtons: true,
    min: 0,
    step: 1,
    required: true,
    placeholder: 'synchronization.placeholders.enter_reality_num',
  },
  {
    prop: 'note',
    label: 'synchronization.table.columns.note',
    width: 180,
    type: 'inputText',
    placeholder: 'synchronization.placeholders.enter_note',
  },
];

export const SYNCHRONIZATION_TABLE_SECTIONS_VIEW: TableSection[] = [
  {
    title: 'synchronization.form.sections.product_details',
    show: true,
    hasCheckbox: false,
    data: [],
    columns: SYNCHRONIZATION_TABLE_COLUMNS,
    actions: [], // No actions in view mode
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function createSynchronizationFormFields(): FormField[] {
  return [...SYNCHRONIZATION_FORM_FIELDS];
}

// View mode form fields (all readonly)
export function createSynchronizationViewFormFields(): FormField[] {
  return SYNCHRONIZATION_FORM_FIELDS.map((field) => ({
    ...field,
    readonly: true,
    disabled: true,
  }));
}

export function getStatusSeverity(
  status: string
): 'success' | 'warning' | 'danger' | 'info' {
  switch (status) {
    case 'approved':
    case 'success':
      return 'success';
    case 'pending':
      return 'warning';
    case 'wait':
      return 'warning';
    case 'rejected':
    case 'fail':
      return 'danger';
    default:
      return 'info';
  }
}

export function getStatusDisplayText(status: string): string {
  switch (status) {
    case 'approved':
      return 'synchronization.status.approved';
    case 'success':
      return 'synchronization.status.success';
    case 'pending':
      return 'synchronization.status.pending';
    case 'rejected':
      return 'synchronization.status.rejected';
    case 'wait':
      return 'synchronization.status.waiting';
    case 'fail':
      return 'synchronization.status.failed';
    default:
      return status;
  }
}

export function getAdjustmentTypeText(type: string): string {
  switch (type) {
    case 'INCREASE':
      return 'synchronization.adjust_type.increase';
    case 'DECREASE':
      return 'synchronization.adjust_type.decrease';
    default:
      return type;
  }
}

export function getAdjustmentTypeClass(type: string): string {
  switch (type) {
    case 'INCREASE':
      return 'bg-green-100 text-green-800';
    case 'DECREASE':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// Status utility functions
export const getStatusStyle = (status: string): any => {
  const severity = getStatusSeverity(status);
  switch (severity) {
    case 'success':
      return {
        'background-color': '#ecfdf5',
        color: '#047857',
        border: 'none',
      };
    case 'warning':
      return {
        'background-color': '#fef3c7',
        color: '#a16207',
        border: 'none',
      };
    case 'danger':
      return {
        'background-color': '#fef2f2',
        color: '#dc2626',
        border: 'none',
      };
    default:
      return {
        'background-color': '#f3f4f6',
        color: '#6b7280',
        border: 'none',
      };
  }
};

// Dialog configuration helpers
export interface DialogConfig {
  title: string;
  message: string;
  buttons: Array<{
    label: string;
    action: string;
    outlined?: boolean;
    severity?: 'success' | 'secondary' | 'info' | 'danger' | 'primary';
  }>;
}

export const createRejectDialogConfig = (
  adjustCode: string,
  translateService: any
): DialogConfig => ({
  title: 'synchronization.dialog.confirm_reject.title',
  message: translateService.instant(
    'synchronization.dialog.confirm_reject.message',
    { adjustCode }
  ),
  buttons: [
    {
      label: 'synchronization.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'synchronization.dialog.actions.reject',
      action: 'confirm',
      severity: 'danger',
    },
  ],
});

export const createDeleteDialogConfig = (
  adjustCode: string,
  translateService: any
): DialogConfig => ({
  title: 'synchronization.dialog.confirm_delete.title',
  message: translateService.instant(
    'synchronization.dialog.confirm_delete.message',
    { adjustCode }
  ),
  buttons: [
    {
      label: 'synchronization.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'synchronization.dialog.actions.delete',
      action: 'confirm',
      severity: 'danger',
    },
  ],
});

export const createBatchDeleteDialogConfig = (
  count: number,
  translateService: any
): DialogConfig => ({
  title: 'synchronization.dialog.confirm_batch_delete.title',
  message: translateService.instant(
    'synchronization.dialog.confirm_batch_delete.message',
    { count }
  ),
  buttons: [
    {
      label: 'synchronization.dialog.actions.cancel',
      action: 'cancel',
      outlined: true,
    },
    {
      label: 'synchronization.dialog.actions.delete',
      action: 'confirm',
      severity: 'danger',
    },
  ],
});
